import { useEffect, useState } from 'react';

const getConnection = () =>
  navigator.connection || navigator.mozConnection || navigator.webkitConnection;

// `navigator.onLine` is the only signal every browser agrees on, so it alone decides
// online vs offline. The Network Information API (`navigator.connection`) is
// Chromium-only and is used purely to add speed/latency detail on top.
//
// This split matters: Chrome reports `downlink: 0` while offline, and treating that
// as "no data available" used to paint the indicator green and label it "Connected"
// exactly when the agent had no network at all.
const readStatus = () => {
  if (navigator.onLine === false) {
    return {
      isOnline: false,
      downlink: 0,
      rtt: 0,
      effectiveType: 'none',
      health: 'Offline',
      color: 'text-danger'
    };
  }

  const conn = getConnection();
  const downlink = conn?.downlink || 0;
  const rtt = conn?.rtt || 0;
  const effectiveType = conn?.effectiveType || 'unknown';
  const base = { isOnline: true, downlink, rtt, effectiveType };

  // Either the browser has no Network Information API (Firefox/Safari) or it has not
  // measured yet. We know we are online, we just cannot claim anything about quality.
  if (!conn || !downlink) {
    return { ...base, health: 'Online', color: 'text-success' };
  }

  if (downlink > 2 && rtt < 300) {
    return { ...base, health: 'Good', color: 'text-success' };
  }
  if (downlink > 0.5 && rtt < 600) {
    return { ...base, health: 'Fair', color: 'text-warning' };
  }
  return { ...base, health: 'Poor', color: 'text-danger' };
};

const useNetworkStatus = () => {
  // Read synchronously so a tab that loads while already offline starts red rather
  // than green — `offline` only fires on a transition, never on load.
  const [status, setStatus] = useState(readStatus);

  useEffect(() => {
    const update = () => setStatus(readStatus());
    const conn = getConnection();

    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    conn?.addEventListener?.('change', update);

    update();

    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
      conn?.removeEventListener?.('change', update);
    };
  }, []);

  return status;
};

export default useNetworkStatus;
